import { Component, type ErrorInfo, type ReactNode } from 'react';

type Props = { children: ReactNode };
type State = { hasError: boolean; error: Error | null };

/**
 * App-root error boundary. Catches render-time exceptions (including those
 * surfaced by lazy chunk load failures in weak-network Telegram WebViews) so
 * the user sees a recoverable fallback instead of a blank screen.
 *
 * Class component because react-error-boundary isn't a project dependency and
 * the CLAUDE.md guidelines disallow adding new libraries without confirmation.
 */
export class ErrorBoundary extends Component<Props, State> {
  state: State = { hasError: false, error: null };

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    // Surface the stack to the console — the Telegram WebView's devtools is
    // the only way to inspect this in production, but at least it shows up.
    // eslint-disable-next-line no-console
    console.error('App error boundary caught:', error, info);
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="app-shell-min-h flex flex-col items-center justify-center gap-4 bg-surface p-6 text-center">
          <h2 className="text-base font-semibold text-app">Something went wrong</h2>
          <p className="max-w-xs text-sm text-app/70">
            {this.state.error?.message ?? 'An unexpected error occurred.'}
          </p>
          <button
            type="button"
            onClick={() => window.location.reload()}
            className="rounded-full bg-brand px-6 py-2 text-sm font-semibold text-white"
          >
            Reload
          </button>
        </div>
      );
    }
    return this.props.children;
  }
}
